import { useState, useEffect } from 'react';
import { Form, Input, Radio, Checkbox, DatePicker, InputNumber, Upload, Button, Table, Tooltip, message, Modal } from 'antd';
import { InfoCircleOutlined, UploadOutlined, PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import type { Question } from '@/services/types/checklist.types';
import { QuestionAnswerType } from '@/services/types/checklist.types';
import { videoSessionService, VideoSessionCaptureService, type VideoSessionCapture } from '@/services/videoSession.service';
import { S3Image } from '@/components/S3Image';
import { ImageEditorModal, type ImageEditorResult } from '@/components/ImageEditorModal';
import type { UploadFile } from 'antd/es/upload/interface';

interface QuestionRendererProps {
  question: Question;
  inspectionItemId?: string;
  isFirstPhotoQuestion?: boolean;
  initialValue?: any;
}

const { TextArea } = Input;

// Wrapper para Upload funcionar como componente controlado pelo Form.Item
// Form.Item passa value/onChange, mas Upload precisa de fileList como prop
function ControlledUpload({ value, onChange, children, ...props }: any) {
  const fileList = value?.fileList || (Array.isArray(value) ? value : []);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState<string>('');

  const handleRemove = (file: UploadFile) => {
    const newFileList = fileList.filter((f: UploadFile) => f.uid !== file.uid);
    onChange?.({ fileList: newFileList });
  };

  // Para arquivos recém-selecionados (sem url/thumbUrl), gera thumbUrl via createObjectURL.
  // Sem isso, o antd abre o preview em about:blank quando o arquivo não tem URL.
  const handleChange = ({ fileList: newFileList }: any) => {
    const enriched = newFileList.map((f: any) => {
      if (!f.url && !f.thumbUrl && f.originFileObj instanceof File && f.originFileObj.type.startsWith('image/')) {
        return { ...f, thumbUrl: URL.createObjectURL(f.originFileObj) };
      }
      return f;
    });
    onChange?.({ fileList: enriched });
  };

  // Sobrescreve preview default (que abre about:blank pra arquivos sem url).
  // Lê o arquivo como dataURL e mostra numa modal.
  const handlePreview = async (file: UploadFile) => {
    let src: string | null = (file as any).thumbUrl || file.url || null;
    if (!src && file.originFileObj instanceof File) {
      src = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file.originFileObj as File);
      });
    }
    if (src) {
      setPreviewSrc(src);
      setPreviewTitle(file.name || 'Preview');
    }
  };

  // Renderizar fotos com S3Image quando têm documentId (URLs pre-signed expiram)
  const itemRender = (originNode: React.ReactNode, file: UploadFile) => {
    const documentId = (file as any).documentId;
    if (documentId && file.status === 'done') {
      return (
        <div style={{ position: 'relative' }}>
          <S3Image
            documentId={documentId}
            alt={file.name}
            width={104}
            height={104}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            preview={true}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => handleRemove(file)}
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              zIndex: 1,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
            }}
          />
        </div>
      );
    }
    return originNode;
  };

  return (
    <>
      <Upload
        {...props}
        fileList={fileList}
        onChange={handleChange}
        onPreview={handlePreview}
        itemRender={itemRender}
      >
        {children}
      </Upload>
      <Modal
        open={!!previewSrc}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewSrc(null)}
        width={800}
      >
        {previewSrc && (
          <img alt={previewTitle} style={{ width: '100%' }} src={previewSrc} />
        )}
      </Modal>
    </>
  );
}

function DynamicTable({ columns, value, onChange }: any) {
  const initialDataSource = value && Array.isArray(value) && value.length > 0
    ? value.map((item: any, index: number) => ({
        key: `${index}`,
        columns: item.columns || {},
      }))
    : [{ key: '0', columns: {} }];

  const [dataSource, setDataSource] = useState(initialDataSource);

  useEffect(() => {
    if (value && Array.isArray(value) && value.length > 0) {
      const newDataSource = value.map((item: any, index: number) => ({
        key: `${index}`,
        columns: item.columns || {},
      }));
      setDataSource(newDataSource);
    }
  }, [value]);

  const handleAddRow = () => {
    const newData = {
      key: `${dataSource.length}`,
      columns: {},
    };
    const newDataSource = [...dataSource, newData];
    setDataSource(newDataSource);
    onChange?.(newDataSource.map((item: any, index: number) => ({
      rowNumber: index + 1,
      columns: item.columns,
    })));
  };

  const handleDeleteRow = (key: string) => {
    const newDataSource = dataSource.filter((item: any) => item.key !== key);
    setDataSource(newDataSource);
    onChange?.(newDataSource.map((item: any, index: number) => ({
      rowNumber: index + 1,
      columns: item.columns,
    })));
  };

  const handleCellChange = (key: string, columnKey: string, value: any) => {
    const newDataSource = dataSource.map((item: any) => {
      if (item.key === key) {
        return {
          ...item,
          columns: {
            ...item.columns,
            [columnKey]: value,
          },
        };
      }
      return item;
    });

    setDataSource(newDataSource);

    const formattedData = newDataSource.map((item: any, index: number) => ({
      rowNumber: index + 1,
      columns: item.columns,
    }));
    onChange?.(formattedData);
  };

  if (!columns || columns.length === 0) {
    return (
      <div style={{ padding: '16px', border: '1px solid #ff4d4f', borderRadius: '4px', backgroundColor: '#fff2f0' }}>
        <p style={{ color: '#ff4d4f', margin: 0 }}>
          ⚠️ Erro: Nenhuma coluna definida para esta tabela. Verifique a configuração do checklist.
        </p>
      </div>
    );
  }

  const tableColumns = columns.map((col: string) => ({
    title: col,
    dataIndex: ['columns', col],
    key: col,
    width: 200,
    render: (_: any, record: any) => (
      <Input
        placeholder={`Digite ${col}`}
        value={record.columns?.[col] || ''}
        onChange={(e) => handleCellChange(record.key, col, e.target.value)}
        style={{ width: '100%', minWidth: '150px' }}
      />
    ),
  }));

  tableColumns.push({
    title: 'Ações',
    key: 'actions',
    render: (_: any, record: any) => (
      <Button
        type="link"
        danger
        icon={<DeleteOutlined />}
        onClick={() => handleDeleteRow(record.key)}
        disabled={dataSource.length === 1}
      />
    ),
  });

  return (
    <div>
      <Table
        columns={tableColumns}
        dataSource={dataSource}
        pagination={false}
        size="small"
        bordered
      />
      <Button
        type="dashed"
        style={{ width: '100%', marginTop: 8 }}
        icon={<PlusOutlined />}
        onClick={handleAddRow}
      >
        Adicionar Linha
      </Button>
    </div>
  );
}
interface PhotoUploadWithCapturesProps {
  inspectionItemId?: string;
  value?: any;
  onChange?: (value: any) => void;
  maxCount?: number;
}

function PhotoUploadWithCaptures({ inspectionItemId, value, onChange, maxCount = 10 }: PhotoUploadWithCapturesProps) {
  const [loading, setLoading] = useState(true);
  const [photos, setPhotos] = useState<UploadFile[]>([]);
  const [editingFile, setEditingFile] = useState<UploadFile | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    if (inspectionItemId) {
      loadCaptures();
    } else {
      setLoading(false);
    }
  }, [inspectionItemId]);

  useEffect(() => {
    if (value?.fileList && Array.isArray(value.fileList)) {
      setPhotos(value.fileList);
    }
  }, [value]);

  const loadCaptures = async () => {
    if (!inspectionItemId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const videoSessions = await videoSessionService.getVideoSessionsByInspectionItemId(inspectionItemId);

      if (!videoSessions || videoSessions.length === 0) {
        setPhotos([]);
        onChange?.({ fileList: [] });
        console.info('[PhotoUploadWithCaptures] loadCaptures concluído', {
          inspectionItemId,
          sessionsCount: 0,
          capturesFetched: 0,
          capturesSkipped: 0,
          filesAttached: 0,
        });
        setLoading(false);
        return;
      }

      const captureService = new VideoSessionCaptureService();
      const allCaptures: VideoSessionCapture[] = [];

      for (const session of videoSessions) {
        try {
          const sessionCaptures = await captureService.getCapturesByVideoSessionId(session.id);
          if (sessionCaptures && sessionCaptures.length > 0) {
            allCaptures.push(...sessionCaptures);
          }
        } catch (err) {
          console.error('[PhotoUploadWithCaptures] Falha ao buscar capturas da sessão', {
            sessionId: session.id,
            error: err,
          });
        }
      }

      let skipped = 0;
      const captureFiles: UploadFile[] = allCaptures
        .map((capture, index) => {
          const documentId = capture.documentId ||
            (capture.fileUrl?.startsWith('docman/') ? capture.fileUrl.replace('docman/', '') : null);

          if (!documentId) {
            skipped++;
            return null;
          }

          return {
            uid: capture.id,
            name: capture.fileName || `Foto ${index + 1}`,
            status: 'done' as const,
            url: capture.fileUrl,
            thumbUrl: capture.fileUrl,
            documentId: documentId,
            isFromVideoConference: true,
          } as UploadFile;
        })
        .filter((item): item is UploadFile => item !== null);

      captureFiles.sort((a, b) => {
        const captureA = allCaptures.find(c => c.id === a.uid);
        const captureB = allCaptures.find(c => c.id === b.uid);
        if (!captureA || !captureB) return 0;
        return new Date(captureB.capturedAt).getTime() - new Date(captureA.capturedAt).getTime();
      });

      setPhotos(captureFiles);

      onChange?.({ fileList: captureFiles });

      console.info('[PhotoUploadWithCaptures] loadCaptures concluído', {
        inspectionItemId,
        sessionsCount: videoSessions.length,
        capturesFetched: allCaptures.length,
        capturesSkipped: skipped,
        filesAttached: captureFiles.length,
      });
    } catch {
      message.error('Erro ao carregar fotos da videoconferência');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = ({ fileList }: any) => {
    const limitedList = fileList.slice(0, maxCount);
    setPhotos(limitedList);
    onChange?.({ fileList: limitedList });
  };

  const handleRemove = async (file: UploadFile) => {
    if ((file as any).isFromVideoConference) {
      return new Promise<boolean>((resolve) => {
        Modal.confirm({
          title: 'Remover Foto da Videoconferência',
          content: 'Esta foto foi capturada durante a videoconferência. Tem certeza que deseja removê-la?',
          okText: 'Sim, remover',
          okType: 'danger',
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              if (!inspectionItemId) {
                throw new Error('inspectionItemId não disponível');
              }

              const videoSessions = await videoSessionService.getVideoSessionsByInspectionItemId(inspectionItemId);
              if (!videoSessions || videoSessions.length === 0) {
                throw new Error('Sessão de vídeo não encontrada');
              }

              const captureService = new VideoSessionCaptureService();
              await captureService.deleteCapture(videoSessions[0]!.id, file.uid);

              const newPhotos = photos.filter(p => p.uid !== file.uid);
              setPhotos(newPhotos);

              if (onChange) {
                onChange({ fileList: newPhotos });
              }

              message.success('Foto removida com sucesso!');
              resolve(true);
            } catch (err: any) {
              message.error(err?.message || 'Erro ao remover foto');
              resolve(false);
            }
          },
          onCancel: () => {
            resolve(false);
          },
        });
      });
    }

    const newPhotos = photos.filter(p => p.uid !== file.uid);
    setPhotos(newPhotos);
    if (onChange) {
      onChange({ fileList: newPhotos });
    }
    return true;
  };

  const handleEditSave = async (result: ImageEditorResult) => {
    if (!editingFile || !inspectionItemId) return;

    try {
      setSavingEdit(true);

      const videoSessions = await videoSessionService.getVideoSessionsByInspectionItemId(inspectionItemId);
      if (!videoSessions || videoSessions.length === 0) {
        throw new Error('Sessão de vídeo não encontrada');
      }

      const captureService = new VideoSessionCaptureService();
      const updatedCapture = await captureService.updateCapture(
        videoSessions[0]!.id,
        editingFile.uid,
        {
          description: result.caption,
          file: result.rotatedFile || undefined,
        }
      );

      const newDocumentId = updatedCapture.documentId ||
        (updatedCapture.fileUrl?.startsWith('docman/') ? updatedCapture.fileUrl.replace('docman/', '') : null);

      const newPhotos = photos.map((p) =>
        p.uid === editingFile.uid
          ? { ...p, name: result.caption || p.name, documentId: newDocumentId || (p as any).documentId }
          : p
      );
      setPhotos(newPhotos);
      onChange?.({ fileList: newPhotos });

      setEditingFile(null);
      message.success('Imagem atualizada com sucesso!');
    } catch (err: any) {
      message.error(err?.message || 'Erro ao atualizar imagem');
    } finally {
      setSavingEdit(false);
    }
  };

  const beforeUpload = (file: File) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('Você pode enviar apenas imagens!');
      return Upload.LIST_IGNORE;
    }

    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('A imagem deve ter menos de 10MB!');
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  const itemRender = (originNode: React.ReactNode, file: UploadFile) => {
    const documentId = (file as any).documentId;

    if (documentId && file.status === 'done') {
      return (
        <div style={{ position: 'relative' }}>
          <S3Image
            documentId={documentId}
            alt={file.name}
            width={104}
            height={104}
            style={{
              objectFit: 'cover',
              borderRadius: 4,
            }}
            preview={true}
          />
          <div style={{ position: 'absolute', top: 4, right: 4, zIndex: 1, display: 'flex', gap: 2 }}>
            {(file as any).isFromVideoConference && (
              <Button
                type="text"
                icon={<EditOutlined />}
                size="small"
                onClick={() => setEditingFile(file)}
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }}
              />
            )}
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => handleRemove(file)}
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }}
            />
          </div>
        </div>
      );
    }

    return originNode;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        Carregando fotos da videoconferência...
      </div>
    );
  }

  return (
    <>
      <Upload
        listType="picture-card"
        fileList={photos}
        onChange={handleChange}
        onRemove={handleRemove}
        beforeUpload={beforeUpload}
        maxCount={maxCount}
        accept="image/*"
        multiple
        itemRender={itemRender}
      >
        {photos.length < maxCount && (
          <div>
            <PlusOutlined />
            <div style={{ marginTop: 8 }}>Upload</div>
          </div>
        )}
      </Upload>

      <ImageEditorModal
        open={!!editingFile}
        onCancel={() => setEditingFile(null)}
        onSave={handleEditSave}
        documentId={(editingFile as any)?.documentId}
        caption={editingFile?.name || ''}
        saving={savingEdit}
      />
    </>
  );
}

const answerTypeMap: Record<string, QuestionAnswerType> = {
  'ShortAnswer': QuestionAnswerType.ShortAnswer,
  'LongAnswer': QuestionAnswerType.LongAnswer,
  'RadioButton': QuestionAnswerType.RadioButton,
  'Checkbox': QuestionAnswerType.Checkbox,
  'YesNo': QuestionAnswerType.YesNo,
  'Date': QuestionAnswerType.Date,
  'NumericInteger': QuestionAnswerType.NumericInteger,
  'NumericDecimal': QuestionAnswerType.NumericDecimal,
  'Currency': QuestionAnswerType.Currency,
  'Table': QuestionAnswerType.Table,
  'Photo': QuestionAnswerType.Photo,
  'File': QuestionAnswerType.File,
};

export function QuestionRenderer({ question, inspectionItemId, isFirstPhotoQuestion, initialValue }: QuestionRendererProps) {
  const answerType = answerTypeMap[question.answerType];

  const renderQuestionInput = () => {
    switch (answerType) {
      case QuestionAnswerType.ShortAnswer:
        return (
          <Input
            placeholder={question.hintText || "Digite sua resposta"}
            maxLength={question.maxCharacters || 500}
            showCount={!!question.maxCharacters}
          />
        );

      case QuestionAnswerType.LongAnswer:
        return (
          <TextArea
            rows={4}
            placeholder={question.hintText || "Digite sua resposta"}
            maxLength={question.maxCharacters || 5000}
            showCount={!!question.maxCharacters}
          />
        );

      case QuestionAnswerType.RadioButton:
        return (
          <Radio.Group>
            {question.options?.map((option) => (
              <Radio key={option} value={option} style={{ display: 'block', marginBottom: 8 }}>
                {option}
              </Radio>
            ))}
          </Radio.Group>
        );

      case QuestionAnswerType.Checkbox:
        return (
          <Checkbox.Group>
            {question.options?.map((option) => (
              <Checkbox key={option} value={option} style={{ marginBottom: 8, marginRight: 12 }}>
                {option}
              </Checkbox>
            ))}
          </Checkbox.Group>
        );

      case QuestionAnswerType.YesNo:
        return (
          <Radio.Group>
            <Radio value="Sim" style={{ display: 'block', marginBottom: 8 }}>Sim</Radio>
            <Radio value="Não" style={{ display: 'block', marginBottom: 8 }}>Não</Radio>
          </Radio.Group>
        );

      case QuestionAnswerType.Date:
        return (
          <DatePicker
            format="DD/MM/YYYY"
            placeholder="Selecione uma data"
            style={{ width: '100%' }}
          />
        );

      case QuestionAnswerType.NumericInteger:
        return (
          <InputNumber
            placeholder="Digite um número"
            style={{ width: '100%' }}
            precision={0}
          />
        );

      case QuestionAnswerType.NumericDecimal:
        return (
          <InputNumber
            placeholder="Digite um número"
            style={{ width: '100%' }}
            precision={2}
            step={0.01}
            decimalSeparator=","
          />
        );

      case QuestionAnswerType.Currency:
        return (
          <InputNumber
            placeholder="Digite um valor"
            style={{ width: '100%' }}
            prefix="R$"
            precision={2}
            step={0.01}
            decimalSeparator=","
          />
        );

      case QuestionAnswerType.Photo:
        if (isFirstPhotoQuestion && inspectionItemId) {
          return (
            <PhotoUploadWithCaptures
              inspectionItemId={inspectionItemId}
              maxCount={question.maxPhotos || 50}
            />
          );
        }

        return (
          <ControlledUpload
            listType="picture-card"
            maxCount={question.maxPhotos || 50}
            accept="image/*"
            beforeUpload={() => false}
          >
            <div>
              <PlusOutlined />
              <div style={{ marginTop: 8 }}>Upload</div>
            </div>
          </ControlledUpload>
        );

      case QuestionAnswerType.File:
        return (
          <ControlledUpload
            maxCount={3}
            accept=".pdf,.doc,.docx,.xls,.xlsx"
            beforeUpload={() => false}
          >
            <Button icon={<UploadOutlined />}>Selecionar Arquivo</Button>
          </ControlledUpload>
        );

      case QuestionAnswerType.Table:
        return <DynamicTable columns={question.tableColumns?.map(col => col.name) || []} />;

      default:
        return (
          <Input
            placeholder={question.hintText || "Digite sua resposta"}
            maxLength={question.maxCharacters || 500}
            showCount={!!question.maxCharacters}
          />
        );
    }
  };

  return (
    <Form.Item
      name={question.id}
      initialValue={initialValue}
      label={
        <span>
          {question.title}
          {question.description && (
            <Tooltip title={question.description}>
              <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
            </Tooltip>
          )}
        </span>
      }
      rules={[
        {
          required: question.isRequired,
          message: `${question.title} é obrigatório`,
        },
        ...(question.maxCharacters ? [{
          max: question.maxCharacters,
          message: `${question.title} deve ter no máximo ${question.maxCharacters} caracteres`,
        }] : []),
      ]}
    >
      {renderQuestionInput()}
    </Form.Item>
  );
}
